import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import gsap from 'gsap'
import { useChat } from '../../context/ChatContext'
import { createAvatarRig } from './rig'
import './index.scss'

// Screen-px offset that parks the avatar just left of the open chat widget
const ATTEND_X = -406
const ENTER_DELAY = 1.9 // seconds — lets the page loader finish first

/**
 * The figure — a compact mech in the site's editorial voice, built for a
 * senior engineer's corner: squared chamfered helmet with a heavy brow
 * ridge over a night-dark visor, wide determined screen-lamp eyes and a
 * resting smirk, gorget collar on a real neck, inverted-triangle chest
 * with pauldron shoulder caps and a seam-lined plate carrying the
 * wax-seal V crest, gauntlet forearms ending in amber ink-drop hands
 * (the living-ink cursor's cousins), and a single strong thruster under
 * the nozzle. The antenna is crowned with an electron orbit — the React
 * badge, worn like a service pin. Arms are painted last so waves and
 * chin-rests stage in front of the face; pauldrons cap the joints above
 * them so rotating arms stay socketed.
 */
const AvatarFigure = () => (
  <svg
    viewBox="0 0 200 240"
    className="vk-avatar__svg"
    role="img"
    aria-label="Animated robot assistant"
  >
    <g data-part="root">
      <ellipse data-part="shadow" cx="100" cy="226" rx="26" ry="4.5" />
      <ellipse className="under-glow" cx="100" cy="226" rx="17" ry="3" />
      <g data-part="float">
        {/* Idle thruster — the flicker tween breathes it */}
        <g data-part="jet">
          <path
            className="flame"
            d="M100,196 C95,202 94,208 96.5,213.5 C98,217 102,217 103.5,213.5
               C106,208 105,202 100,196 Z"
          />
          <path
            className="flame-core"
            d="M100,199 C97.8,203 97.5,207 99,210.5 C99.6,212 100.4,212 101,210.5
               C102.5,207 102.2,203 100,199 Z"
          />
        </g>

        {/* Neck — painted first; helmet and gorget overlap it */}
        <rect className="shell" x="92" y="100" width="16" height="16" rx="3" />

        {/* Gorget collar */}
        <rect className="collar" x="84" y="112" width="32" height="9" rx="3" />

        {/* Chest — inverted triangle, chamfered */}
        <path
          className="shell"
          d="M72,118 L128,118 Q136,118 135.5,126 L131,162 Q129,174 117,177
             L106,179.5 Q100,181 94,179.5 L83,177 Q71,174 69,162
             L64.5,126 Q64,118 72,118 Z"
        />

        {/* Plate seam */}
        <path className="panel-line" d="M78,158 L122,158" />

        {/* Pelvis block + thruster nozzle */}
        <rect className="shell" x="90" y="179" width="20" height="11" rx="3" />
        <rect className="nozzle" x="94" y="190" width="12" height="5" rx="1.5" />

        {/* Wax-seal crest */}
        <circle className="seal" cx="100" cy="141" r="12.5" />
        <text className="monogram" x="100" y="147.5" textAnchor="middle">V</text>

        {/* Head */}
        <g data-part="head">
          <g data-part="antenna">
            <path className="antenna-stem" d="M100,46 L100,31" />
            <g data-part="antenna-tip">
              <ellipse
                className="orbit"
                cx="100"
                cy="26"
                rx="8"
                ry="3.2"
                transform="rotate(-20 100 26)"
              />
              <circle className="antenna-tip" cx="100" cy="26" r="4.2" />
            </g>
          </g>

          {/* Helmet — squared, chamfered, flat-topped */}
          <path
            className="shell"
            d="M56,96 L56,58 Q56,46 70,46 L130,46 Q144,46 144,58 L144,96
               Q144,106 128,106 L72,106 Q56,106 56,96 Z"
          />

          {/* Brow ridge — a heavy ink bar over the glass */}
          <rect className="brow-bar" x="62" y="52" width="76" height="5" rx="2.5" />

          {/* Visor — night-dark in every theme, like the sidebar */}
          <rect className="visor" x="62" y="56" width="76" height="40" rx="10" />
          <path className="visor-shine" d="M69,64 Q69.5,59 75.5,57.5" />

          {/* Screen-lamp eyes — wide, level, determined; the lamp glances */}
          <g data-part="eye-l">
            <ellipse className="eye-glow" cx="83" cy="74" rx="12" ry="10" />
            <g data-part="pupil">
              <rect className="lamp" x="75.5" y="68.5" width="15" height="11" rx="3" />
              <circle className="glint" cx="87.4" cy="71.2" r="1.3" />
            </g>
          </g>
          <g data-part="eye-r">
            <ellipse className="eye-glow" cx="117" cy="74" rx="12" ry="10" />
            <g data-part="pupil">
              <rect className="lamp" x="109.5" y="68.5" width="15" height="11" rx="3" />
              <circle className="glint" cx="121.4" cy="71.2" r="1.3" />
            </g>
          </g>

          {/* Brows — inner ends set low: locked in */}
          <path data-part="brow-l" className="face-line face-line--brow" d="M75,60 Q83,59 91,63.5" />
          <path data-part="brow-r" className="face-line face-line--brow" d="M109,63.5 Q117,59 125,60" />
          {/* Resting smirk */}
          <path data-part="mouth" className="face-line" d="M92,89.5 Q100,92.5 108,86.5" />
        </g>

        {/* Arms — ready stance; gauntlet forearms; in front so gestures read */}
        <g data-part="arm-l">
          <path className="limb" d="M74,122 Q64,128 60,142" />
          <g data-part="forearm-l">
            <path className="limb limb--fore" d="M60,142 Q57,150 58,158" />
            <g data-part="hand-l">
              <path
                className="hand-drop"
                d="M58,154.5 L65,164.5 A7.9,7.9 0 1 1 51,164.5 Z"
              />
            </g>
          </g>
        </g>
        <g data-part="arm-r">
          <path className="limb" d="M126,122 Q136,128 140,142" />
          <g data-part="forearm-r">
            <path className="limb limb--fore" d="M140,142 Q143,150 142,158" />
            <g data-part="hand-r">
              <path
                className="hand-drop"
                d="M142,154.5 L149,164.5 A7.9,7.9 0 1 1 135,164.5 Z"
              />
            </g>
          </g>
        </g>

        {/* Pauldrons — cap the shoulder joints so rotating arms stay socketed */}
        <circle className="shell pauldron" cx="72" cy="124" r="9" />
        <circle className="shell pauldron" cx="128" cy="124" r="9" />
      </g>
    </g>
  </svg>
)

const AvatarCompanion = () => {
  const location = useLocation()
  const { isOpen, isLoading, toggleChat } = useChat()

  const [isWide, setIsWide] = useState(
    () => window.matchMedia('(min-width: 1200px)').matches
  )

  const containerRef = useRef(null)
  const rigRef = useRef(null)
  const timersRef = useRef({ idle: null, wander: null })
  const enteredRef = useRef(false)
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen

  const isAiPage = location.pathname === '/ai'
  const active = isWide && !isAiPage

  // ── Rig lifecycle ───────────────────────────────────────────────────────

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1200px)')
    const onChange = (e) => setIsWide(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!active || !container) return undefined

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const rig = createAvatarRig(container.querySelector('svg'), { reducedMotion })
    rigRef.current = rig
    if (process.env.NODE_ENV === 'development') window.__vkRig = rig

    rig.startAmbient()

    // Occasional idle micro-gestures keep it alive
    const armIdleTimer = () => {
      timersRef.current.idle = setTimeout(() => {
        if (!isOpenRef.current && document.visibilityState === 'visible') {
          rig.playRandomIdleGesture()
        }
        armIdleTimer()
      }, gsap.utils.random(7000, 14000))
    }

    // Rarely, drift along the bottom edge and come back (shimeji-style)
    const armWanderTimer = () => {
      timersRef.current.wander = setTimeout(() => {
        if (!isOpenRef.current && document.visibilityState === 'visible') {
          rig.wander(container)
        }
        armWanderTimer()
      }, gsap.utils.random(40000, 85000))
    }

    // Opening night: pop up from the corner, wave hello, act cute — the
    // robot speaks only through gestures (speech bubbles felt intrusive)
    const entranceCall = gsap.delayedCall(reducedMotion ? 0.6 : ENTER_DELAY, () => {
      const entrance = rig.enter(container, { firstTime: true })
      entrance.then(() => {
        enteredRef.current = true
        if (reducedMotion) return
        const hello = rig.wave()
        if (hello) hello.then(() => rig.flex())
        armIdleTimer()
        armWanderTimer()
      })
    })

    // Eyes (and a hint of head) follow the cursor
    const onMouseMove = (e) => {
      const r = container.getBoundingClientRect()
      const cx = r.left + r.width / 2
      const cy = r.top + r.height * 0.35 // eye line, not container center
      const nx = gsap.utils.clamp(-1, 1, (e.clientX - cx) / (window.innerWidth / 2))
      const ny = gsap.utils.clamp(-1, 1, (e.clientY - cy) / (window.innerHeight / 2))
      rig.lookAt(nx, ny)
    }
    window.addEventListener('mousemove', onMouseMove, { passive: true })

    const timers = timersRef.current
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      entranceCall.kill()
      clearTimeout(timers.idle)
      clearTimeout(timers.wander)
      rig.destroy()
      gsap.killTweensOf(container)
      rigRef.current = null
      enteredRef.current = false
      if (process.env.NODE_ENV === 'development') delete window.__vkRig
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  // ── Chat choreography ───────────────────────────────────────────────────

  // When the widget opens, the avatar slides over to "attend" the conversation;
  // when it closes, it drifts back to its post.
  useEffect(() => {
    const container = containerRef.current
    const rig = rigRef.current
    if (!container || !rig || !enteredRef.current) return
    gsap.killTweensOf(container) // a wander may be mid-flight
    if (isOpen) {
      gsap.to(container, { x: ATTEND_X, y: 0, scale: 0.82, duration: 0.7, ease: 'power3.inOut' })
      rig.nod()
    } else {
      gsap.to(container, { x: 0, y: 0, scale: 1, duration: 0.7, ease: 'power3.inOut' })
      rig.cheer()
    }
  }, [isOpen])

  // While the AI is composing, the avatar thinks along
  useEffect(() => {
    const rig = rigRef.current
    if (!rig || !enteredRef.current) return
    rig.setThinking(isLoading)
    if (!isLoading && isOpenRef.current) rig.nod() // a reply just landed
  }, [isLoading])

  if (!active) return null

  return (
    <div ref={containerRef} className="vk-avatar" aria-live="off">
      <button
        className="vk-avatar__hit"
        onClick={toggleChat}
        aria-label={isOpen ? 'Close AI assistant chat' : 'Open AI assistant chat'}
        data-cursor={isOpen ? 'close chat' : 'say hi'}
      >
        <AvatarFigure />
      </button>
    </div>
  )
}

export default AvatarCompanion
